import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import stlModelsData from '../data/stl_models.json';
import * as amplitude from '@amplitude/analytics-browser';

const StlModelDetail = ({ theme }) => {
  const { id } = useParams();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('3d'); // '3d' or '2d'
  const [loadError, setLoadError] = useState(false);

  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelMeshRef = useRef(null);

  // Define colors based on theme
  const bgColor = theme === 'light' ? '#f8f8f8' : '#333333';
  const textColor = theme === 'light' ? '#333' : 'white';

  // Load model data
  useEffect(() => {
    const foundModel = stlModelsData.models.find(m => m.id === parseInt(id));
    if (foundModel) {
      setModel(foundModel);
      
      // Track page view with model details
      amplitude.track('Page Viewed', {
        page: 'STL Model Detail',
        theme: theme,
        modelId: foundModel.id,
        modelName: foundModel.name,
        author: foundModel.author
      });
    } else {
      // Track model not found
      amplitude.track('Model Not Found', {
        modelId: id
      });
    }
    setLoading(false);
  }, [id, theme]);

  // Generate the image path based on the model filename
  const getImagePath = () => {
    if (!model) return '';
    // Use the same name as the STL file but with .png extension
    const baseName = model.filename.replace(/\.stl$/i, '.png');
    return `/stl_files/${baseName}`;
  };

  // Handle image loading error
  const handleImageError = (e) => {
    console.error(`Failed to load thumbnail for ${model?.filename}`);
    e.target.src = getFallbackImage();
    
    // Track image loading error
    amplitude.track('Model Detail Thumbnail Error', {
      modelId: model?.id,
      modelName: model?.name,
      filename: model?.filename
    });
  };

  // Determine model type for fallback image
  const getModelType = () => {
    if (!model) return 'generic';

    const filename = model.filename.toLowerCase();
    const tags = model.tags ? model.tags.map(tag => tag.toLowerCase()) : [];

    if (filename.includes('keychain') || tags.some(tag => tag.includes('keychain'))) {
      return 'keychain';
    } else if (filename.includes('fidget') || tags.some(tag => tag.includes('fidget'))) {
      return 'fidget';
    } else if (filename.includes('dino') || filename.includes('t-rex') || tags.some(tag => tag.includes('dinosaur'))) {
      return 'dinosaur';
    } else if (filename.includes('spiral') || tags.some(tag => tag.includes('spiral'))) {
      return 'spiral';
    } else {
      return 'generic';
    }
  };

  // Get fallback image based on model type
  const getFallbackImage = () => {
    const modelType = getModelType();
    return `/images/model_types/${modelType}.svg`;
  };

  // Set up 3D viewer
  useEffect(() => {
    if (!model || viewMode !== '3d' || !mountRef.current) return;

    // Set up scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(theme === 'light' ? 0xf8f8f8 : 0x333333);
    sceneRef.current = scene;

    // Set up camera
    const camera = new THREE.PerspectiveCamera(
      45,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    // Start with a reasonable default position
    camera.position.z = 10;
    cameraRef.current = camera;

    // Set up renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Set up controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.enableZoom = true;
    controls.minDistance = 0.1;
    controls.maxDistance = 100;
    controls.autoRotate = false;
    controlsRef.current = controls;

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
    backLight.position.set(-1, -1, -1);
    scene.add(backLight);

    // Set model color based on theme
    const modelColor = theme === 'light' ? 0x2196F3 : 0xffffff;

    // Create material with theme-appropriate color
    const material = new THREE.MeshStandardMaterial({
      color: modelColor,
      metalness: 0.2,
      roughness: 0.6,
    });

    // Load STL model with proper error handling
    const loader = new STLLoader();
    const filePath = `/stl_files/${encodeURIComponent(model.filename)}`;

    try {
      loader.load(
        filePath,
        (geometry) => {
          try {
            if (!geometry || !geometry.attributes || !geometry.attributes.position) {
              throw new Error('Invalid geometry loaded');
            }

            // Create mesh with material
            const mesh = new THREE.Mesh(geometry, material);

            // Center the model
            geometry.computeBoundingBox();
            const boundingBox = geometry.boundingBox;

            if (!boundingBox) {
              throw new Error('Unable to compute bounding box');
            }

            // Center the model at origin
            const center = new THREE.Vector3();
            boundingBox.getCenter(center);
            geometry.translate(-center.x, -center.y, -center.z);

            // Calculate size for proper scaling and camera positioning
            const size = new THREE.Vector3();
            boundingBox.getSize(size);
            const maxDim = Math.max(size.x, size.y, size.z);

            if (maxDim === 0 || !isFinite(maxDim)) {
              throw new Error('Invalid model dimensions');
            }

            // Use a moderate scale that works for most models
            const scale = 20 / maxDim;
            mesh.scale.set(scale, scale, scale);

            // Remove any existing model
            if (modelMeshRef.current) {
              scene.remove(modelMeshRef.current);
            }

            scene.add(mesh);
            modelMeshRef.current = mesh;

            // Position camera based on model size for optimal viewing
            // Use the bounding sphere to calculate optimal camera distance
            geometry.computeBoundingSphere();
            const boundingSphere = geometry.boundingSphere;

            // Calculate camera position based on bounding sphere
            const radius = boundingSphere.radius * scale;
            const cameraDistance = radius * 2.5; // Adjust this factor as needed

            // Position camera at a distance that ensures the model is visible
            camera.position.set(0, 0, cameraDistance);
            camera.lookAt(0, 0, 0);

            // Update controls
            controls.target.set(0, 0, 0);
            controls.update();

            // Force a render to update the view
            renderer.render(scene, camera);
          } catch (err) {
            console.error(`Error processing STL geometry for ${model.filename}:`, err);
            setLoadError(true);
          }
        },
        // Progress callback
        (xhr) => {
          console.log(`${model.filename} ${(xhr.loaded / xhr.total) * 100}% loaded`);
        },
        // Error callback
        (error) => {
          console.error(`Error loading STL for ${model.filename}:`, error);
          setLoadError(true);
        }
      );
    } catch (error) {
      console.error(`Exception trying to load STL for ${model.filename}:`, error);
      setLoadError(true);
    }

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      if (controlsRef.current) {
        controlsRef.current.update();
      }

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current || !cameraRef.current || !rendererRef.current) return;

      cameraRef.current.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };

    window.addEventListener('resize', handleResize);

    // Store the mount element for cleanup
    const currentMount = mountRef.current;

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);

      if (rendererRef.current && currentMount) {
        currentMount.removeChild(rendererRef.current.domElement);
      }

      if (rendererRef.current) {
        rendererRef.current.dispose();
      }

      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
    };
  }, [model, viewMode, theme]);

  if (loading) {
    return (
      <div className="container" style={{ color: textColor, padding: '20px' }}>
        <div className="center-align">
          <div className="preloader-wrapper big active">
            <div className="spinner-layer" style={{ borderColor: textColor }}>
              <div className="circle-clipper left">
                <div className="circle"></div>
              </div>
              <div className="gap-patch">
                <div className="circle"></div>
              </div>
              <div className="circle-clipper right">
                <div className="circle"></div>
              </div>
            </div>
          </div>
          <h4>Loading model...</h4>
        </div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="container" style={{ color: textColor, padding: '20px' }}>
        <div className="center-align">
          <h3>Model not found</h3>
          <p>The requested model could not be found.</p>
          <Link to="/stl-models" className="btn blue">
            <i className="material-icons left">arrow_back</i>
            Back to Models
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ color: textColor, padding: '20px' }}>
      <div className="row">
        <div className="col s12">
          <Link 
            to="/stl-models" 
            className="btn-flat" 
            style={{ color: theme === 'light' ? '#1e88e5' : '#64b5f6' }}
            onClick={() => amplitude.track('Navigation', {
              from: 'Model Detail',
              to: 'STL Models Gallery',
              modelId: model.id,
              modelName: model.name
            })}
          >
            <i className="material-icons left">arrow_back</i>
            Back to Models
          </Link>
          <h3 style={{ marginTop: '10px' }}>{model.name}</h3>
          <div className="chip" style={{ backgroundColor: theme === 'light' ? '#e0e0e0' : '#444' }}>
            <i className="material-icons left" style={{ marginRight: '5px' }}>person</i>
            {model.author}
          </div>
          <div className="chip" style={{ backgroundColor: theme === 'light' ? '#e0e0e0' : '#444' }}>
            <i className="material-icons left" style={{ marginRight: '5px' }}>date_range</i>
            {model.date}
          </div>
          <div className="chip" style={{ backgroundColor: theme === 'light' ? '#e0e0e0' : '#444' }}>
            <i className="material-icons left" style={{ marginRight: '5px' }}>file_download</i>
            {model.downloads} downloads
          </div>
          <div className="chip" style={{ backgroundColor: theme === 'light' ? '#e0e0e0' : '#444' }}>
            <i className="material-icons left" style={{ marginRight: '5px' }}>favorite</i>
            {model.likes} likes
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col s12 m9 l10">
          <div className="card" style={{ backgroundColor: theme === 'light' ? 'white' : '#2d2d2d', borderRadius: '5px' }}>
            <div className="card-content">
              <div className="row" style={{ marginBottom: '0' }}>
                <div className="col s12" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span className="card-title" style={{ margin: '0' }}>Model Preview</span>
                  <div className="switch-field" style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      className={`btn-small ${viewMode === '3d' ? 'blue' : 'grey'}`}
                      style={{ marginRight: '10px' }}
                      onClick={() => {
                        if (viewMode !== '3d') {
                          setViewMode('3d');
                          
                          // Track view mode change
                          amplitude.track('View Mode Changed', {
                            modelId: model.id,
                            modelName: model.name,
                            fromMode: '2d',
                            toMode: '3d',
                            reason: 'user_selection'
                          });
                        }
                      }}
                    >
                      <i className="material-icons left">view_in_ar</i>
                      3D
                    </button>
                    <button
                      className={`btn-small ${viewMode === '2d' ? 'blue' : 'grey'}`}
                      onClick={() => {
                        if (viewMode !== '2d') {
                          setViewMode('2d');
                          
                          // Track view mode change
                          amplitude.track('View Mode Changed', {
                            modelId: model.id,
                            modelName: model.name,
                            fromMode: '3d',
                            toMode: '2d',
                            reason: 'user_selection'
                          });
                        }
                      }}
                    >
                      <i className="material-icons left">image</i>
                      2D
                    </button>
                  </div>
                </div>
              </div>

              <div
                style={{
                  height: '500px',
                  position: 'relative',
                  backgroundColor: bgColor,
                  borderRadius: '5px',
                  overflow: 'hidden'
                }}
              >
                {viewMode === '3d' ? (
                  <>
                    <div ref={mountRef} style={{ width: '100%', height: '100%' }}></div>
                    {loadError && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                          zIndex: 10
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <i className="material-icons" style={{ fontSize: '3rem' }}>error_outline</i>
                          <p>Failed to load 3D model</p>
                          <button
                            className="btn-small blue"
                            onClick={() => {
                              setViewMode('2d');
                              
                              // Track view mode switch after error
                              amplitude.track('View Mode Changed', {
                                modelId: model.id,
                                modelName: model.name,
                                fromMode: '3d',
                                toMode: '2d',
                                reason: 'error'
                              });
                            }}
                          >
                            Switch to 2D View
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src={getImagePath()}
                      alt={model.name}
                      onError={handleImageError}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="card-action">
              <a
                href={`/stl_files/${model.filename}`}
                download
                className="btn waves-effect waves-light blue"
                onClick={() => amplitude.track('Model Detail Download', {
                  modelId: model.id,
                  modelName: model.name,
                  filename: model.filename,
                  author: model.author,
                  currentView: viewMode
                })}
              >
                <i className="material-icons left">file_download</i>
                Download STL
              </a>
              <a
                href="#!"
                className="btn-flat waves-effect"
                style={{ color: theme === 'light' ? '#1e88e5' : '#64b5f6' }}
                onClick={() => amplitude.track('Model Liked', {
                  modelId: model.id,
                  modelName: model.name,
                  author: model.author
                })}
              >
                <i className="material-icons left">favorite_border</i>
                Like
              </a>
              <a
                href="#!"
                className="btn-flat waves-effect"
                style={{ color: theme === 'light' ? '#1e88e5' : '#64b5f6' }}
                onClick={() => amplitude.track('Model Shared', {
                  modelId: model.id,
                  modelName: model.name,
                  author: model.author
                })}
              >
                <i className="material-icons left">share</i>
                Share
              </a>
              <a
                href="#!"
                className="btn waves-effect waves-light green right"
                style={{ marginLeft: '10px' }}
                onClick={() => amplitude.track('Model Order Clicked', {
                  modelId: model.id,
                  modelName: model.name,
                  filename: model.filename,
                  author: model.author,
                  fromPage: 'model_detail'
                })}
              >
                <i className="material-icons left">shopping_cart</i>
                Order Print
              </a>
            </div>
          </div>

          <div className="row">
            <div className="col s12">
              <div className="card" style={{ backgroundColor: theme === 'light' ? 'white' : '#2d2d2d', borderRadius: '5px' }}>
                <div className="card-content">
                  <span className="card-title">Print Settings</span>
                  <div className="row">
                    <div className="col s12 m6">
                      <table className="responsive-table" style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                          <tr style={{ lineHeight: '1.2' }}>
                            <td style={{ padding: '4px 5px' }}><strong>Printer:</strong></td>
                            <td style={{ padding: '4px 5px' }}>Any FDM printer</td>
                          </tr>
                          <tr style={{ lineHeight: '1.2' }}>
                            <td style={{ padding: '4px 5px' }}><strong>Rafts:</strong></td>
                            <td style={{ padding: '4px 5px' }}>No</td>
                          </tr>
                          <tr style={{ lineHeight: '1.2' }}>
                            <td style={{ padding: '4px 5px' }}><strong>Infill:</strong></td>
                            <td style={{ padding: '4px 5px' }}>20%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="col s12 m6">
                      <table className="responsive-table" style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                          <tr style={{ lineHeight: '1.2' }}>
                            <td style={{ padding: '4px 5px' }}><strong>Supports:</strong></td>
                            <td style={{ padding: '4px 5px' }}>No</td>
                          </tr>
                          <tr style={{ lineHeight: '1.2' }}>
                            <td style={{ padding: '4px 5px' }}><strong>Resolution:</strong></td>
                            <td style={{ padding: '4px 5px' }}>0.2mm</td>
                          </tr>
                          <tr style={{ lineHeight: '1.2' }}>
                            <td style={{ padding: '4px 5px' }}><strong>Material:</strong></td>
                            <td style={{ padding: '4px 5px' }}>PLA recommended</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col s12 m3 l2">
          <div className="card" style={{ backgroundColor: theme === 'light' ? 'white' : '#2d2d2d', borderRadius: '5px' }}>
            <div className="card-content">
              <span className="card-title">Description</span>
              <p style={{ whiteSpace: 'pre-line' }}>{model.description}</p>

              {model.tags && model.tags.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h6>Tags</h6>
                  <div>
                    {model.tags.map((tag, index) => (
                      <div
                        key={index}
                        className="chip"
                        style={{ backgroundColor: theme === 'light' ? '#e0e0e0' : '#444' }}
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StlModelDetail;
